import { initializeApp, getApps } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, User, browserLocalPersistence } from 'firebase/auth';
import { ENV } from '@/config/environment';
import { isNative, setStorageItem, getStorageItem, removeStorageItem } from '@/lib/capacitor';
import { getFirebaseAuthErrorMessage } from '@/lib/firebaseAuthErrors';

const FIREBASE_AUTH_BACKUP_KEY = 'firebase_auth_active';

// Firebase configuration
const firebaseConfig = {
  apiKey: ENV.FIREBASE.API_KEY,
  authDomain: ENV.FIREBASE.AUTH_DOMAIN,
  projectId: ENV.FIREBASE.PROJECT_ID,
  storageBucket: ENV.FIREBASE.STORAGE_BUCKET,
  messagingSenderId: ENV.FIREBASE.MESSAGING_SENDER_ID,
  appId: ENV.FIREBASE.APP_ID,
};

// Initialize Firebase only if no apps exist
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
// Use initializeAuth (not getAuth) so we can OMIT the browserPopupRedirectResolver.
// getAuth's default resolver loads a gapi iframe to the Firebase authDomain, which never
// completes under the iOS WKWebView `capacitor://localhost` origin — that hang is what left
// onAuthStateChanged (and the app's loading screen) stuck forever. Only email/password is
// used, so no popup/redirect resolver is needed. IndexedDB persistence (falling back to
// localStorage) survives refreshes. try/catch guards HMR/double-import re-init.
export const auth = (() => {
  try {
    return initializeAuth(app, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] });
  } catch {
    return getAuth(app);
  }
})();

// Promise that resolves once Firebase has finished restoring the auth session.
// This prevents API calls from firing before currentUser is available.
let _resolveAuthReady: () => void;
export const authReady = new Promise<void>((resolve) => {
  _resolveAuthReady = resolve;
});
const unsubInitial = auth.onAuthStateChanged(() => {
  _resolveAuthReady();
  unsubInitial(); // only need the first callback
});

export class AuthService {
  async signIn(email: string, password: string): Promise<{ user: User; token: string }> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get the ID token (JWT)
      const token = await user.getIdToken();

      // Persist auth indicator in native storage as a backup
      // in case the WebView's IndexedDB is cleared unexpectedly
      if (isNative()) {
        await setStorageItem(FIREBASE_AUTH_BACKUP_KEY, 'true');
      }
      
      return { user, token };
    } catch (error) {
      throw new Error(getFirebaseAuthErrorMessage(error, 'login'));
    }
  }

  async signOut(): Promise<void> {
    try {
      if (isNative()) {
        await removeStorageItem(FIREBASE_AUTH_BACKUP_KEY);
      }
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error(getFirebaseAuthErrorMessage(error, 'passwordReset'));
    }
  }

  async getCurrentUser(): Promise<User | null> {
    return auth.currentUser;
  }

  async getCurrentToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: User | null) => void) {
    return auth.onAuthStateChanged(callback);
  }

  /**
   * Check if the user was previously authenticated on native but Firebase
   * lost the session (e.g., IndexedDB cleared by the OS).
   * Returns true if the user should be prompted to re-login.
   */
  async wasAuthUnexpectedlyCleared(): Promise<boolean> {
    if (!isNative()) return false;
    const backup = await getStorageItem(FIREBASE_AUTH_BACKUP_KEY);
    return backup === 'true' && auth.currentUser === null;
  }

  /**
   * Clear the auth backup flag (call after user has re-authenticated
   * or after handling the unexpected logout).
   */
  async clearAuthBackup(): Promise<void> {
    if (isNative()) {
      await removeStorageItem(FIREBASE_AUTH_BACKUP_KEY);
    }
  }
}

export const authService = new AuthService();
